// React.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import { TempDbUsageSession } from '../../../types/instance/TempDb';

// Components.
import ReactTable from "../../ReactTable";

// Helper.
import { getDefaultPageSize, getToolTipPosition } from '../../../helpers/utils';
import StatementToolTip from "./StatementToolTip";
import Helper from "../../../helpers/Helper";
import { FilterType } from "../../../typescript/Enums";
import SearchInput from '../../SearchInput';

export default function TempDbUsageTable(props: {
    error: string | null,
    loading: boolean,
    data: TempDbUsageSession[],
    instance: InstanceTarget,
    setFilterOptions: Function
}) {
    const { error, loading, data, instance } = props;
    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    // CSV headers
    const headers: any[] = useMemo(() => {
        return [
            {
                label: 'Session',
                key: 'session'
            },
            {
                label: 'Login Time',
                key: 'logintime'
            },
            {
                label: 'Client',
                key: 'client'
            },
            {
                label: 'Username',
                key: 'username'
            },
            {
                label: 'Program',
                key: 'program'
            },
            {
                label: '#',
                key: 'sqlhash'
            },
            {
                label: 'SQL Text',
                key: 'sqltext'
            },
            {
                label: 'User Objects Alloc',
                key: 'userobjectsallocpagecount'
            },
            {
                label: 'User Objects Dealloc',
                key: 'userobjectsdeallocpagecount'
            },
            {
                label: 'Internal Objects Alloc',
                key: 'internalobjectsallocpagecount'
            },
            {
                label: 'Internal Objects Dealloc',
                key: 'internalobjectsdeallocpagecount'
            },
            {
                label: 'User Objects Deferred Dealloc',
                key: 'userobjectsdeferreddeallocpagecount'
            }
        ]
    }, []);

    const goToActivityPageWithSession = (sessionId: string) => {
        const options = Helper.getFilterParams()
        props.setFilterOptions(Helper.updateFilterOptions(FilterType.Sessions, sessionId, options));
    }

    // Table columns
    const columns: Column[] = useMemo(() => {
        return [
            {
                Header: 'Session',
                accessor: 'session',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic',
                Cell: ({ cell }: any) => {
                    return (<Link onClick={() => goToActivityPageWithSession(cell.value)} to={`/instances/${instance.id}/activity?sessions=${cell.value}`}>{cell.value}</Link>)
                }
            },
            {
                Header: 'Login Time',
                accessor: 'logintime',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Client',
                accessor: 'client',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Username',
                accessor: 'username',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Database',
                accessor: 'database',
                headerClassName: 'text',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: false
            },
            {
                Header: 'Program',
                accessor: 'program',
                headerClassName: 'text',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: false
            },
            {
                Header: 'User Objects Alloc',
                accessor: 'userobjectsallocpagecount',
                headerClassName: 'text text-wrap',
                className: 'text',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: true
            },
            {
                Header: 'User Objects Dealloc',
                accessor: 'userobjectsdeallocpagecount',
                headerClassName: 'text text-wrap',
                className: 'text',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: true
            },
            {
                Header: 'Internal Objects Alloc',
                accessor: 'internalobjectsallocpagecount',
                headerClassName: 'text text-wrap',
                className: 'text',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: true
            },
            {
                Header: 'Internal Objects Dealloc',
                accessor: 'internalobjectsdeallocpagecount',
                headerClassName: 'text text-wrap',
                className: 'text',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: true
            },
            {
                Header: 'User Objects Deferred Dealloc',
                accessor: 'userobjectsdeferreddeallocpagecount',
                headerClassName: 'text text-wrap',
                className: 'text',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: false
            }
        ]
    }, [instance.id]);

    const tableData = useMemo(() =>
            data.filter((row) =>
                row.session.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.logintime.toLowerCase().includes(tableFilter.toLowerCase())),
        [data, tableFilter]);

    // Transform function to escape double quotes in SQL text for CSV export
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        const transformed = JSON.parse(JSON.stringify(csvData));
        transformed.forEach((item: any) => {
            if (item.sqltext) {
                item.sqltext = item.sqltext.replaceAll('"', '""');
            }
        });
        return transformed;
    }, []);

    const tableInstance = useTable(
            {
                columns,
                data: tableData,
                initialState:
                    {
                        pageIndex: initialPageIndex,
                        pageSize: initialPageSize,
                    },
                disableSortRemove: true,
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            }, useSortBy, usePagination);

    const
        {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            prepareRow,
            page,
            setPageSize,
            pageOptions,
            pageCount,
            canPreviousPage,
            previousPage,
            canNextPage,
            nextPage,
            gotoPage,
            state: { pageIndex, pageSize }
        } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    function clearTableFilter() {
        setTableFilter('');
    }


    // The bracket must be present and on the same line as the return.
    return (
        <div className="card">
            <div className="card-header">
                <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
                Session Usage (pages)
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapse-usage" aria-expanded="false" aria-controls="collapse-usage"/>
            </div>
            <div id="collapse-usage" className="card-body collapse show">
                <div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="Temp DB Usage"
                        transformData={transformCsvData}
                        headers={headers}
                    />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            ref={inputRef}
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <ReactTable
                    getTableProps={getTableProps}
                    getTableBodyProps={getTableBodyProps}
                    headerGroups={headerGroups}
                    prepareRow={prepareRow}
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    dataLength={data.length}
                    error={error}
                    loading={loading}
                    renderCell={(cell) => {
                        if (cell.column.id === "sqltext") {
                            return (
                                <div className='tooltip-scroll ellipsis'
                                    onMouseEnter={(event) => setVisibleToolTip({
                                        id: cell.row.values.logintime,
                                        position: getToolTipPosition(event)
                                    })}
                                    onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                                >
                                    {cell.render('Cell')}
                                    {cell.row.values.logintime === visibleToolTip.id && cell.row.values.sqlhash && (
                                        <StatementToolTip instanceType={props.instance.type}
                                            position={visibleToolTip.position}
                                            id={cell.row.values.logintime}
                                            link={`/instances/${props.instance.id}/activity/statement/${cell.row.values.sqlhash}/text`}
                                            cell={cell} />
                                    )}
                                </div>
                            );
                        }
                        return cell.render('Cell');
                    }}
                />
            </div>
        </div>
    )
}
